import React, { useState, useMemo } from "react";
import { useTableData } from "../../hooks/useTableData";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ─────────────────────────────────────────────────────────────────────────────
// TABLE CONFIG
// ─────────────────────────────────────────────────────────────────────────────
export const upiQrReportConfig = {
  title: "UPI QR Reports",
  apiKey: "data",
  searchKeys: [
    "merchant_vpa",
    "merchant_name",
    "order_id",
    "txn_id",
    "amount",
    "status"
  ],
  actions: { view: false, edit: true, delete: false, export: true },
  columns: [
    { key: "id", label: "ID" },
    { key: "BC Name", label: "BC Name" },
    { key: "Phone", label: "Phone" },
    { key: "order_id", label: "Reference" },
    { key: "txn_id", label: "TXN ID" },
    { key: "merchant_name", label: "Merchant" },
    { key: "merchant_vpa", label: "UPI VPA" },
    { key: "note", label: "Note" },
    { key: "amount", label: "Amount", type: "currency" },
    {
      key: "status",
      label: "Status",
      type: "badge",
      badgeClass: (v) => {
        const map = {
          success: "badge bg-success",
          failed: "badge bg-danger",
          pending: "badge bg-warning"
        };
        return map[v?.toLowerCase()] || "badge bg-secondary";
      },
      transform: (v) => v?.toUpperCase()
    },
    { key: "Opening Balance", label: "Opening Bal.", type: "currency" },
    { key: "Commission", label: "Commission", type: "currency" },
    { key: "TDS", label: "TDS", type: "currency" },
    { key: "Closing Balance", label: "Closing Bal.", type: "currency" },
    { key: "commission", label: "Commission (D)", type: "currency" },
    { key: "tds", label: "TDS (D)", type: "currency" },
    { key: "Opening Balance BC", label: "Opening Bal. BC", type: "currency" },
    { key: "BC Commission", label: "BC Commission", type: "currency" },
    { key: "BC TDS", label: "BC TDS", type: "currency" },
    { key: "Closing balance BC", label: "Closing Bal. BC", type: "currency" },
    { key: "Opening Balance MAdmin", label: "Opening Bal. MAdmin", type: "currency" },
    { key: "Commission MAdmin", label: "Commission MAdmin", type: "currency" },
    { key: "TDS MAdmin", label: "TDS MAdmin", type: "currency" },
    { key: "Closing balance MAdmin", label: "Closing Bal. MAdmin", type: "currency" },
    { key: "UTR", label: "UTR" },
    { key: "Description", label: "Description" },
    { key: "created_at", label: "Date & Time" }
  ]
};

// ─────────────────────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending: { bg: "#fff8e1", color: "#f59f00", border: "#ffe082", label: "Pending" },
    success: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7", label: "Success" },
    failed: { bg: "#fce4ec", color: "#c62828", border: "#ef9a9a", label: "Failed" }
  };
  const s = map[status?.toLowerCase()] ?? map.pending;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.4,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`
      }}
    >
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INFO ROW — used inside modal detail card
// ─────────────────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
      borderBottom: "1px solid #f0f0f0",
      fontSize: 13
    }}
  >
    <span style={{ color: "#888", minWidth: 130 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#1a1a1a", textAlign: "right" }}>
      {value || "—"}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN UPDATE MODAL
// ─────────────────────────────────────────────────────────────────────────────
const AdminUpdateModal = ({ isOpen, onClose, report, onSuccess }) => {
  const [status, setStatus] = useState("success");
  const [utr, setUtr] = useState("");
  const [txnId, setTxnId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!isOpen || !report) return null;

  const reset = () => {
    setUtr("");
    setTxnId("");
    setError("");
    setStatus("success");
    setDone(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!utr.trim()) {
      setError("UTR number is required.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("report_id", report.id);
      fd.append("status", status);
      fd.append("utr", utr.trim());
      fd.append("txn_id", txnId.trim());

      const res = await fetchWrapper(
        "https://pro.mokshiq.in/admin_update_upi_qr/",
        { method: "POST", body: fd }
      );

      if (res?.status) {
        setDone(true);
        setTimeout(() => {
          onSuccess(report.id, status);
          handleClose();
        }, 1400);
      } else {
        setError(res?.message || "Update failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = status === "success";

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,10,20,0.55)",
        backdropFilter: "blur(3px)",
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 520,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          overflow: "hidden",
          fontFamily: "'Segoe UI', sans-serif"
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              Update UPI QR Payment
            </div>
            <div
              style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, marginTop: 2 }}
            >
              Transaction #{report.id}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: 8,
              width: 32,
              height: 32,
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: isSuccess ? "#e8f5e9" : "#fce4ec",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 14px",
                  fontSize: 30
                }}
              >
                {isSuccess ? "✓" : "✗"}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 17,
                  color: isSuccess ? "#2e7d32" : "#c62828"
                }}
              >
                Marked as {isSuccess ? "Success" : "Failed"}
              </div>
              <div style={{ color: "#888", fontSize: 13, marginTop: 5 }}>
                Wallets have been {isSuccess ? "confirmed" : "reversed"}.
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  background: "#f8f9fc",
                  borderRadius: 10,
                  padding: "12px 16px",
                  marginBottom: 20,
                  border: "1px solid #ebebeb"
                }}
              >
                {report["BC Name"] && (
                  <InfoRow label="BC Name" value={report["BC Name"]} />
                )}
                <InfoRow label="Merchant Name" value={report.merchant_name} />
                <InfoRow label="UPI VPA" value={report.merchant_vpa} />
                <InfoRow label="Note" value={report.note} />
                <InfoRow
                  label="Amount"
                  value={
                    report.amount
                      ? `₹${Number(report.amount).toLocaleString("en-IN", {
                          minimumFractionDigits: 2
                        })}`
                      : null
                  }
                />
                <InfoRow label="Reference" value={report.order_id} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 8,
                    fontSize: 13
                  }}
                >
                  <span style={{ color: "#888" }}>Current Status</span>
                  <StatusBadge status={report.status} />
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#444",
                    display: "block",
                    marginBottom: 8
                  }}
                >
                  New Status <span style={{ color: "#e53935" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: 10 }}>
                  {["success", "failed"].map((s) => (
                    <div
                      key={s}
                      onClick={() => setStatus(s)}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        padding: "11px 0",
                        borderRadius: 10,
                        cursor: "pointer",
                        border: `2px solid ${
                          status === s
                            ? s === "success"
                              ? "#43a047"
                              : "#e53935"
                            : "#e0e0e0"
                        }`,
                        background:
                          status === s
                            ? s === "success"
                              ? "#e8f5e9"
                              : "#fce4ec"
                            : "#fafafa",
                        color:
                          status === s
                            ? s === "success"
                              ? "#2e7d32"
                              : "#c62828"
                            : "#9e9e9e",
                        fontWeight: 700,
                        fontSize: 14,
                        transition: "all 0.15s",
                        userSelect: "none"
                      }}
                    >
                      {s === "success" ? "✓ Approve" : "✗ Reject"}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#444",
                    display: "block",
                    marginBottom: 6
                  }}
                >
                  UTR Number <span style={{ color: "#e53935" }}>*</span>
                </label>
                <input
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  placeholder="Enter UTR / bank reference number"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: `1.5px solid ${error && !utr ? "#e53935" : "#dde1e7"}`,
                    borderRadius: 9,
                    fontSize: 14,
                    outline: "none",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#444",
                    display: "block",
                    marginBottom: 6
                  }}
                >
                  External TXN ID{" "}
                  <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 6 }}>
                    (optional)
                  </span>
                </label>
                <input
                  value={txnId}
                  onChange={(e) => setTxnId(e.target.value)}
                  placeholder="Bank / gateway transaction ID"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid #dde1e7",
                    borderRadius: 9,
                    fontSize: 14,
                    outline: "none",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              {status === "failed" && (
                <div
                  style={{
                    background: "#fff8e1",
                    border: "1px solid #ffe082",
                    borderRadius: 9,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#e65100",
                    marginBottom: 16,
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start"
                  }}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                  <span>
                    Rejecting this payment will{" "}
                    <strong>reverse all wallet deductions</strong> for every level
                    in the hierarchy. This action <strong>cannot be undone</strong>.
                  </span>
                </div>
              )}

              {error && (
                <div
                  style={{
                    background: "#fce4ec",
                    border: "1px solid #ef9a9a",
                    borderRadius: 9,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#b71c1c",
                    marginBottom: 16
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "11px 0",
                    borderRadius: 9,
                    border: "1.5px solid #dde1e7",
                    background: "#fff",
                    color: "#555",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    flex: 2,
                    padding: "11px 0",
                    borderRadius: 9,
                    border: "none",
                    background: isSuccess
                      ? "linear-gradient(135deg, #388e3c, #43a047)"
                      : "linear-gradient(135deg, #c62828, #e53935)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.8 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8
                  }}
                >
                  {loading && (
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        animation: "upiSpin 0.7s linear infinite",
                        display: "inline-block"
                      }}
                    />
                  )}
                  {loading
                    ? "Updating…"
                    : isSuccess
                    ? "✓ Approve Payment"
                    : "✗ Reject Payment"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes upiSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const UpiQrReportsPage = () => {
  const { userid, role } = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
  const isAdmin = userRole === "Admin" || userRole === "Main Admin";

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const getTodayRange = () => {
    const t = new Date().toISOString().split("T")[0];
    return { start: t, end: t };
  };
  const { start, end } = getTodayRange();

  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);

  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/upi_qr_reports_data?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  const tableData = useTableData(apiUrl, upiQrReportConfig, refreshKey);

  const {
    loading: tableLoading,
    error: tableError,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortField,
    sortDirection,
    handleSort,
    selectedRows,
    handleRowSelect,
    handleSelectAll,
    filters,
    handleFilterChange,
    paginatedData,
    sortedData,
    totalPages,
    data
  } = tableData;

  const handleUpdateSuccess = (reportId, newStatus) => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleDateSubmit = (e) => {
    if (e) e.preventDefault();
    const newUrl = `https://pro.mokshiq.in/upi_qr_reports_data?user=${userid}&filter_date=${startDate}&end_date=${endDate}`;
    setApiUrl(newUrl);
    setRefreshKey((prev) => prev + 1);
  };

  const handleEdit = (report) => {
    if (!isAdmin) return;
    if (report.status?.toLowerCase() !== "pending") {
      alert(
        `This transaction is already "${report.status}" and cannot be updated.`
      );
      return;
    }
    setSelectedReport(report);
    setShowModal(true);
  };

  const filteredConfig = useMemo(() => {
    const base = {
      ...upiQrReportConfig,
      columns: [...upiQrReportConfig.columns],
      actions: {
        ...upiQrReportConfig.actions,
        edit: isAdmin
      }
    };

    const roleColumns = {
      Retailer: [
        "id",
        "order_id",
        "merchant_name",
        "merchant_vpa",
        "note",
        "amount",
        "status",
        "Opening Balance",
        "Commission",
        "TDS",
        "Closing Balance",
        "created_at"
      ],
      "API Partner": [
        "id",
        "order_id",
        "merchant_name",
        "merchant_vpa",
        "note",
        "amount",
        "status",
        "Opening Balance",
        "Commission",
        "TDS",
        "Closing Balance",
        "created_at"
      ],
      Distributer: [
        "id",
        "order_id",
        "merchant_name",
        "merchant_vpa",
        "amount",
        "status",
        "commission",
        "tds",
        "created_at"
      ],
      "Master Distributor": [
        "id",
        "order_id",
        "merchant_name",
        "merchant_vpa",
        "amount",
        "status",
        "commission",
        "tds",
        "created_at"
      ],
      "White label": [
        "id",
        "BC Name",
        "Phone",
        "order_id",
        "merchant_name",
        "merchant_vpa",
        "note",
        "amount",
        "status",
        "Opening Balance",
        "Commission",
        "TDS",
        "Closing Balance",
        "created_at"
      ],
      Admin: [
        "id",
        "BC Name",
        "Phone",
        "order_id",
        "txn_id",
        "merchant_name",
        "merchant_vpa",
        "note",
        "amount",
        "status",
        "Opening Balance",
        "Commission",
        "TDS",
        "Closing Balance",
        "Opening Balance BC",
        "BC Commission",
        "BC TDS",
        "Closing balance BC",
        "UTR",
        "Description",
        "created_at"
      ],
      "Main Admin": [
        "id",
        "BC Name",
        "Phone",
        "order_id",
        "txn_id",
        "merchant_name",
        "merchant_vpa",
        "note",
        "amount",
        "status",
        "Opening Balance",
        "Commission",
        "TDS",
        "Closing Balance",
        "Opening Balance BC",
        "BC Commission",
        "BC TDS",
        "Closing balance BC",
        "Opening Balance MAdmin",
        "Commission MAdmin",
        "TDS MAdmin",
        "Closing balance MAdmin",
        "UTR",
        "Description",
        "created_at"
      ]
    };

    const allowed = roleColumns[userRole] ?? [];
    base.columns = base.columns.filter((c) => allowed.includes(c.key));
    return base;
  }, [userRole, isAdmin]);

  const handleExport = (type = "excel") => {
    try {
      let exportData =
        selectedRows.length > 0
          ? data.filter((item) =>
              selectedRows.includes(item.id || item[filteredConfig.idField])
            )
          : sortedData;

      if (!exportData || exportData.length === 0) {
        alert("No data available to export");
        return;
      }

      const formattedData = exportData.map((row) => {
        const obj = {};
        filteredConfig.columns.forEach((col) => {
          obj[col.label] = row[col.key];
        });
        return obj;
      });

      if (type === "excel") {
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "UPI QR Reports");
        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([buffer]),
          `UPI_QR_Reports_${startDate}_to_${endDate}.xlsx`
        );
      }
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "N/A";
    const num = parseFloat(amount);
    if (isNaN(num)) return "N/A";
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (column.type === "currency") return formatCurrency(value);
    if (column.type === "badge") {
      const bgClass = column.badgeClass?.(value) || "bg-secondary";
      return (
        <span
          className={`badge ${bgClass} rounded-pill px-2.5 py-1 fw-semibold`}
          style={{ fontSize: "11px" }}
        >
          {column.transform
            ? column.transform(value)
            : String(value).toUpperCase()}
        </span>
      );
    }
    return column.transform ? column.transform(value) : String(value);
  };

  const totalRecordsCount = sortedData?.length || 0;
  const startItem =
    totalRecordsCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalRecordsCount);

  return (
    <main>
      <div className="container-fluid">
        {(loading || tableLoading) && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.75)", zIndex: 4000 }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Branded Hero Banner Matching Design */}
        <div
          className="card border-0 mb-3 overflow-hidden position-relative shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3 px-3.5 position-relative z-1">
            <div className="row align-items-center mb-2">
              <div className="col-12 col-md-8">
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      backgroundColor: "#7e57c2",
                      color: "#ffffff",
                      marginRight: "18px"
                    }}
                  >
                    <i
                      className="ph-duotone ph-qr-code"
                      style={{ fontSize: "22px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="fw-extrabold mb-0.5"
                      style={{ color: "#0f172a", fontSize: "18px" }}
                    >
                      UPI QR Payment Reports
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                      Monitor and approve dynamic QR & UPI collection transactions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4 text-end d-none d-md-block">
                <img
                  src="/assets/images/upi_qr_report_3d.png"
                  alt="UPI QR Reports 3D Illustration"
                  style={{
                    maxHeight: "85px",
                    objectFit: "contain",
                    filter: "drop-shadow(0px 6px 12px rgba(126, 87, 194, 0.18))"
                  }}
                />
              </div>
            </div>

            {/* Date Filter Integrated Floating Bar */}
            <form onSubmit={handleDateSubmit}>
              <div
                className="bg-white p-2 px-3 rounded-3 shadow-xs border border-light-subtle d-flex flex-wrap align-items-center gap-2 justify-content-between"
                style={{ borderRadius: "12px" }}
              >
                <div className="d-flex flex-wrap align-items-center gap-2.5 flex-grow-1">
                  <div
                    className="d-flex flex-column flex-grow-1"
                    style={{ minWidth: "160px", maxWidth: "240px" }}
                  >
                    <label
                      className="form-label text-secondary fw-semibold mb-0.5"
                      style={{ fontSize: "11px" }}
                    >
                      From Date
                    </label>
                    <div className="position-relative">
                      <input
                        type="date"
                        className="form-control rounded-2 border-light-subtle shadow-xs pe-2 py-1"
                        style={{
                          fontSize: "12.5px",
                          height: "34px",
                          backgroundColor: "#f8fafc"
                        }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-center text-muted mx-2"
                    style={{ marginTop: "16px" }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#e0e7ff",
                        color: "#7e57c2"
                      }}
                    >
                      <i
                        className="ph-bold ph-arrow-right"
                        style={{ fontSize: "12px" }}
                      ></i>
                    </div>
                  </div>

                  <div
                    className="d-flex flex-column flex-grow-1"
                    style={{ minWidth: "160px", maxWidth: "240px" }}
                  >
                    <label
                      className="form-label text-secondary fw-semibold mb-0.5"
                      style={{ fontSize: "11px" }}
                    >
                      To Date
                    </label>
                    <div className="position-relative">
                      <input
                        type="date"
                        className="form-control rounded-2 border-light-subtle shadow-xs pe-2 py-1"
                        style={{
                          fontSize: "12.5px",
                          height: "34px",
                          backgroundColor: "#f8fafc"
                        }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-2 mt-sm-0 align-self-end">
                  <button
                    type="submit"
                    className="btn text-white fw-bold d-inline-flex align-items-center justify-content-center gap-1.5 px-3.5 shadow-xs"
                    style={{
                      background:
                        "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                      borderRadius: "10px",
                      height: "34px",
                      minWidth: "120px",
                      fontSize: "13px"
                    }}
                  >
                    <i
                      className="ph-bold ph-paper-plane-tilt"
                      style={{ fontSize: "14px" }}
                    ></i>
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Admin hint banner */}
        {isAdmin && (
          <div
            className="alert border-0 rounded-3 mb-3 shadow-xs d-flex align-items-center gap-2.5 px-3.5 py-2.5"
            style={{
              backgroundColor: "#f3e8ff",
              color: "#6b21a8",
              borderLeft: "4px solid #9333ea"
            }}
          >
            <i
              className="ph-duotone ph-info"
              style={{ fontSize: "20px", color: "#7e57c2" }}
            ></i>
            <span style={{ fontSize: "13px" }}>
              Click <strong>Action (Edit)</strong> on any <strong>Pending</strong>{" "}
              UPI QR transaction to <strong>Approve</strong> or{" "}
              <strong>Reject</strong> it.
            </span>
          </div>
        )}

        {/* Reports Table Container Card */}
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden mb-4">
          {/* Card Header Matching Design */}
          <div className="card-header bg-white p-3.5 border-bottom border-light-subtle">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{
                    width: "38px",
                    height: "38px",
                    backgroundColor: "#f3e8ff",
                    color: "#7e57c2",
                    marginRight: "16px"
                  }}
                >
                  <i
                    className="ph-duotone ph-qr-code"
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                <div>
                  <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "16px" }}>
                    UPI QR Payment Reports
                  </h6>
                  <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                    Total {totalRecordsCount} records found
                  </p>
                </div>
              </div>

              {/* Right Header Action Controls */}
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                <div className="position-relative" style={{ minWidth: "220px" }}>
                  <i
                    className="ph-bold ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                    style={{ fontSize: "14px" }}
                  ></i>
                  <input
                    type="text"
                    className="form-control rounded-pill ps-5 pe-3 py-1.5 border-light-subtle shadow-xs"
                    style={{ fontSize: "13px" }}
                    placeholder="Search Merchant, VPA, Ref, Txn ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <select
                  className="form-select rounded-3 py-1.5 px-3 border-light-subtle shadow-xs"
                  style={{ width: "75px", fontSize: "13px" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <button
                  type="button"
                  className="btn p-2 rounded-3 text-white d-flex align-items-center justify-content-center"
                  style={{ width: "36px", height: "36px", backgroundColor: "#7e57c2" }}
                  title="Filter Options"
                >
                  <i className="ph-bold ph-funnel" style={{ fontSize: "16px" }}></i>
                </button>

                <button
                  type="button"
                  className="btn p-2 rounded-3 border border-light-subtle d-flex align-items-center justify-content-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#f8fafc",
                    color: "#7e57c2"
                  }}
                  onClick={() => handleExport("excel")}
                  title="Export Excel"
                >
                  <i
                    className="ph-bold ph-download-simple"
                    style={{ fontSize: "16px" }}
                  ></i>
                </button>
              </div>
            </div>
          </div>

          {/* Table Body & Empty State */}
          <div className="card-body p-0">
            <div className="table-responsive app-scroll">
              <table className="table align-middle mb-0 text-nowrap">
                <thead style={{ backgroundColor: "#f5efff", color: "#334155" }}>
                  <tr>
                    <th style={{ width: "40px" }} className="ps-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={
                          selectedRows.length === paginatedData.length &&
                          paginatedData.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </th>
                    {filteredConfig.columns.map((column) => (
                      <th
                        key={column.key}
                        onClick={() =>
                          column.sortable !== false && handleSort(column.key)
                        }
                        style={{
                          cursor:
                            column.sortable !== false ? "pointer" : "default",
                          fontSize: "12.5px",
                          fontWeight: "700",
                          color: "#334155",
                          padding: "12px 14px"
                        }}
                      >
                        <div className="d-flex align-items-center gap-1">
                          <span>{column.label}</span>
                          {column.sortable !== false && (
                            <i
                              className="ph-bold ph-caret-up-down text-muted"
                              style={{ fontSize: "11px" }}
                            ></i>
                          )}
                        </div>
                      </th>
                    ))}
                    {isAdmin && (
                      <th
                        style={{
                          fontSize: "12.5px",
                          fontWeight: "700",
                          color: "#334155",
                          padding: "12px 14px"
                        }}
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData && paginatedData.length > 0 ? (
                    paginatedData.map((item) => (
                      <tr
                        key={item.id || item[filteredConfig.idField]}
                        className="border-bottom border-light-subtle"
                      >
                        <td className="ps-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedRows.includes(
                              item.id || item[filteredConfig.idField]
                            )}
                            onChange={() =>
                              handleRowSelect(
                                item.id || item[filteredConfig.idField]
                              )
                            }
                          />
                        </td>
                        {filteredConfig.columns.map((column) => (
                          <td
                            key={column.key}
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {formatCellValue(item[column.key], column)}
                          </td>
                        ))}
                        {isAdmin && (
                          <td style={{ padding: "12px 14px" }}>
                            {item.status?.toLowerCase() === "pending" ? (
                              <button
                                type="button"
                                className="btn btn-sm d-inline-flex align-items-center justify-content-center p-1.5 rounded-3 text-white"
                                style={{ backgroundColor: "#7e57c2" }}
                                title="Update UPI QR Payment"
                                onClick={() => handleEdit(item)}
                              >
                                <i
                                  className="ph-bold ph-pencil-simple"
                                  style={{ fontSize: "14px" }}
                                ></i>
                              </button>
                            ) : (
                              <span
                                className="text-muted fw-semibold"
                                style={{ fontSize: "11px" }}
                              >
                                Processed
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : null}
                </tbody>
              </table>

              {/* Empty State Matching Design */}
              {(!paginatedData || paginatedData.length === 0) && (
                <div className="text-center py-5 px-3">
                  <div className="mb-3 d-inline-flex align-items-center justify-content-center position-relative">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "72px",
                        height: "72px",
                        backgroundColor: "#f3e8ff",
                        color: "#7e57c2"
                      }}
                    >
                      <i
                        className="ph-duotone ph-file-search"
                        style={{ fontSize: "36px" }}
                      ></i>
                    </div>
                  </div>
                  <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "16px" }}>
                    No records found
                  </h6>
                  <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                    Try adjusting your search or filter criteria
                  </p>
                  <div className="d-flex justify-content-center w-100">
                    <button
                      type="button"
                      className="btn rounded-pill px-3.5 py-1.5 fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
                      style={{
                        border: "1px solid #d8b4fe",
                        backgroundColor: "#fdf4ff",
                        color: "#7e57c2",
                        fontSize: "13px",
                        width: "auto",
                        maxWidth: "180px",
                        display: "inline-flex"
                      }}
                      onClick={() => {
                        setSearchTerm("");
                        const { start, end } = getTodayRange();
                        setStartDate(start);
                        setEndDate(end);
                        const newUrl = `https://pro.mokshiq.in/upi_qr_reports_data?user=${userid}&filter_date=${start}&end_date=${end}`;
                        setApiUrl(newUrl);
                        setRefreshKey((prev) => prev + 1);
                      }}
                    >
                      <i
                        className="ph-bold ph-arrows-counter-clockwise"
                        style={{ fontSize: "14px" }}
                      ></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table Footer Matching Design */}
          <div className="card-footer bg-white p-3 border-top border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3">
            <span className="text-muted" style={{ fontSize: "13px" }}>
              Showing {startItem} to {endItem} of {totalRecordsCount} entries
            </span>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: "12.5px",
                  backgroundColor: currentPage === 1 ? "#f8fafc" : "#f1f5f9",
                  color: currentPage === 1 ? "#94a3b8" : "#7e57c2"
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                <i className="ph-bold ph-caret-left" style={{ fontSize: "12px" }}></i>
                Previous
              </button>

              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: "12.5px",
                  backgroundColor:
                    currentPage >= totalPages || totalPages === 0
                      ? "#f8fafc"
                      : "#f1f5f9",
                  color:
                    currentPage >= totalPages || totalPages === 0
                      ? "#94a3b8"
                      : "#7e57c2"
                }}
                disabled={currentPage >= totalPages || totalPages === 0}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
                <i
                  className="ph-bold ph-caret-right"
                  style={{ fontSize: "12px" }}
                ></i>
              </button>
            </div>
          </div>
        </div>

        {/* Admin update modal */}
        {isAdmin && (
          <AdminUpdateModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              setSelectedReport(null);
            }}
            report={selectedReport}
            onSuccess={handleUpdateSuccess}
          />
        )}
      </div>
    </main>
  );
};

export default UpiQrReportsPage;